import React, { useState, Suspense, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Sphere, MeshDistortMaterial } from '@react-three/drei';
import { FaGithub, FaExternalLinkAlt, FaSearch, FaFileAlt } from 'react-icons/fa';
import SEO from '../components/SEO/SEO';

const ParticlesSphere = () => (
  <Sphere args={[1, 100, 200]} scale={2.5}>
    <MeshDistortMaterial
      color="#4f46e5"
      attach="material"
      distort={0.6}
      speed={2}
      roughness={0.2}
      metalness={0.8}
    />
  </Sphere>
);

const projects = [
    {
    id: 1,
    title: "Food Delivery Web APP",
    description: "A modern, responsive food delivery web app.",
    image: "https://res.cloudinary.com/dqovjmmlx/image/upload/f_auto,q_auto/xuawimmzpnbmrgd88sx1",
    category: "Frontend",
    technologies: ["React", "Tailwind", "framer-motion", "react-router-dom",],
    githubLink: "https://github.com/mahmud-r-farhan/Realtime-Location-Tracker",
    liveLink: "https://beefood.netlify.app/",
    features: ["Restaurants", "Food", "Delivery"]
  },
  {
    id: 2,
    title: "Real-Time Location Tracker",
    description: "The Real-Time Location Tracker is a web app for tracking device locations in real-time.",
    image: "https://res.cloudinary.com/dqovjmmlx/image/upload/v1739883223/aaaaaaaa_hnn63s.jpg",
    category: "Real Time",
    technologies: ["EJS", "CSS", "Node.js", "Socket.io", "Express", "WebRTC"],
    githubLink: "https://github.com/mahmud-r-farhan/Realtime-Location-Tracker",
    liveLink: "https://realtime-location-tracker-v9ow.onrender.com/",
    postLink: "https://x.com/TheJSDev/status/1870321044755521951",
    features: ["Realtime Location", "Audio Communication", "Admin Dashboard"]
  },
  {
    id: 3,
    title: "Authentication System",
    description: "MERN stack application that implements an advanced authentication system with email verification.",
    image: "https://www.thesslstore.com/blog/wp-content/uploads/2021/05/client-authentication-certificate-feature.jpg",
    category: "Full Stack",
    technologies: ["React", "Tailwind", "nodemailer", "Node.js", "MongoDB", "JWT", "bcrypt", "validator",],
    githubLink: "https://github.com/mahmud-r-farhan/mern-auth-system",
    liveLink: "https://mern-auth-system-12tm.vercel.app/",
    features: ["Email Verification", "Password Reset", "Advanced Authentication System"]
  },  
  {
    id: 4,
    title: "AnonyPost: A Social Media App for Anonymous Sharing",
    description: "Anonypost is an anonymous social media app that allows users to share thoughts without needing to log in or provide personal details, enabling secure and simple interactions.",
    image: "https://res.cloudinary.com/dydnhyxfh/image/upload/v1739213254/anonymous-posts/srzmnmbktm5dm4lcsxhh.webp",
    category: "Full Stack",
    technologies: ["React", "Tailwind","Node.js", "Express", "MongoDB","Cloudinary"],
    githubLink: "https://github.com/mahmud-r-farhan/AnonyPost",
    liveLink: "https://anonypost-app.vercel.app",
    features: ["Social Plartform", "Share thoughts", "Interact with others"]
  },
    {
    id: 5,
    title: "AnonyChat: Anonymous Group Chat",
    description: "AnonyChat: A real-time anonymous group chat application with modern UI and privacy features.",
    image: "https://res.cloudinary.com/dqovjmmlx/image/upload/v1740414324/anony-chat_1_cenkfm.jpg",
    category: "Real Time",
    technologies: ["React", "Tailwind", "Socket.io-client", "Node.js", "Express", "MongoDB", "Socket.io"],
    githubLink: "https://github.com/mahmud-r-farhan/AnonyChat",
    liveLink: "https://anonychat-app.vercel.app",
    features: ["Social Plartform", "Share thoughts", "Interact with others"]
  },
  {
    id: 6,
    title: "Customer Service Center",
    description: "Customer Service Center Application is a web-based system designed to manage customer registration, token generation, serial number tracking, and agent-assisted consultation.",
    image: "https://dev-to-uploads.s3.amazonaws.com/uploads/articles/o35bt4gp4v4f5m7a1jor.jpg",
    category: "Full Stack",
    technologies: ["React","Tailwind", "Framer Motion","Node.js", "Express", "MongoDB",],
    githubLink: "https://github.com/mahmud-r-farhan/Customer-Service-Center",
    liveLink: "https://customer-service-center.vercel.app",
    features: ["Consultation Management", "Customer Service", "SAAS"]
  },
  {
    id: 7,
    title: "ModernMart - E-Commerce",
    description: "A modern eCommerce platform offers secure shopping, seamless checkout, and efficient order management for a smooth user experience",
    image: "https://res.cloudinary.com/dqovjmmlx/image/upload/v1740311428/f18f1b500ef384693ce8947bf68dbddb_flpzca.jpg",
    category: "Frontend",
    technologies: ["Next","React","Tailwind", "GSAP","Sonner"],
    githubLink: "https://github.com/mahmud-r-farhan/",
    liveLink: "https://modernmart-mern.vercel.app/",
    features: ["E-commerce", "Customer Service", "Product Sell"]
  },
  {
    id: 8,
    title: "Shopfinity - A store with infinite possibilities",
    description: "Discover Shopfinity, the ultimate online store with limitless products and unbeatable deals. Shop now!",
    image: "https://res.cloudinary.com/dqovjmmlx/image/upload/v1740392128/banner23_cfuhuk.jpg",
    category: "Full Stack",
    technologies: ["React","Bootstrap", "zustand","Node.js", "Express", "MongoDB",],
    githubLink: "https://github.com/mahmud-r-farhan/",
    liveLink: "https://shopfinity-mern.vercel.app",
    features: ["E-commerce", "Customer Service", "Online Store"]
  },
  //
  // Add more projects...
];

const categories = ["All", "Full Stack", "Frontend", "Real Time"];

const Projects = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [visibleProjects, setVisibleProjects] = useState(6);

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === 'All' || project.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white">
      <SEO 
        title="Mahmudur Rahman - Projects"
        description="Full Stack Developer specializing in MERN stack development. Building modern web applications with React, Node.js, and MongoDB."
        image="https://res.cloudinary.com/dqovjmmlx/image/upload/v1740488993/portfolio-banner_1_dsweeg.jpg"
        url="https://devplus.fun/projects"
        keywords="Full Stack Developer, MERN Stack, React, Node.js, MongoDB, Web Development, JavaScript, Next.js, Express.js, MERN Projects, Free Github MERN Projects, Full stacks projects, frontend react nect js projects, backend node js express projects, real time projects, socket io projects, full stack projects, saas, software, github repositories, free projects, free mern projects, free full stack projects, free frontend projects, free backend projects, free real time projects, free saas projects, free software projects, free github repositories, social media projects, github repositories, free github repositories, free social media projects, free social media projects github repositories, chat app projects, chat app projects github repositories, chat app projects free, codes, Location tracker project. github, npm, node package manager, node js, express js, mongodb, mongoose, socket io, web rtc, ejs, react, tailwind, framer motion, react router dom, jwt, bcrypt, validator, cloudinary, gsap, zustand, bootstrap, next js, dev.to, github, social media, chat app, location tracker, authentication system, food delivery, ecommerce, customer service center, shopfinity, modernmart"
      />
      <div className="absolute inset-0 z-0">
        <Canvas>
          <Suspense fallback={null}>
            <OrbitControls enableZoom={false} />
            <ambientLight intensity={0.5} />
            <directionalLight position={[-2, 5, 2]} intensity={1} />
            <ParticlesSphere />
          </Suspense>
        </Canvas>
      </div>

      <div className="relative z-10 container mx-auto px-4 py-16">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl mt-8 font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent mb-4">
            Featured Projects
          </h1>
          <p className="text-xl text-gray-300">
            Exploring the intersection of design and technology
          </p>
        </motion.div>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-12">
          <div className="relative w-full md:w-96">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/10 backdrop-blur-lg rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            {categories.map((category) => (
              <motion.button
                key={category}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 rounded-full ${
                  activeCategory === category
                    ? 'bg-blue-500 text-white'
                    : 'bg-white/10 text-gray-300 hover:bg-white/20'
                }`}
              >
                {category}
              </motion.button>
            ))}
          </div>
        </div>

        {/* Projects Grid */}
        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence>
            {filteredProjects.slice(0, visibleProjects).map((project) => (
              <motion.div
                key={project.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9 }}
                whileHover={{ y: -10 }}
                className="bg-white/10 backdrop-blur-lg rounded-xl overflow-hidden"
              >
                <div className="relative group">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-between p-4">
                  <a
                      href={project.githubLink}
                      target="_blank"
                      title='Github Repository'
                      rel="noopener noreferrer"
                      className="text-white hover:text-blue-400 hover:scale-110 transition-transform duration-300"
                    >
                      <FaGithub size={24} />
                    </a>
                    <a
                      href={project.liveLink}
                      target="_blank"
                      title='Live Link'
                      rel="noopener noreferrer"
                      className="text-white hover:text-blue-400 hover:scale-110 transition-transform duration-300"
                    >
                      <FaExternalLinkAlt size={24} />
                    </a>
                    {project.postLink && (
                      <a
                        href={project.postLink}
                        target="_blank"
                        title='Post Link'
                        rel="noopener noreferrer"
                        className="text-white hover:text-blue-400 hover:scale-110 duration-300 transition-colors"
                      >
                        <FaFileAlt size={24} />
                      </a>
                    )}

                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2">{project.title}</h3>
                  <p className="text-gray-400 mb-4">{project.description}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.technologies.map((tech) => (
                      <span
                        key={tech}
                        className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Load More Button */}
        {filteredProjects.length > visibleProjects && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setVisibleProjects(prev => prev + 6)}
            className="mx-auto mt-12 block px-8 py-3 bg-blue-500 rounded-full hover:bg-blue-600 transition-colors"
          >
            Load More
          </motion.button>
        )}
      </div>
    </div>
  );
};

export default Projects;
